import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  Trash2,
  LogOut,
  Lock,
  ShieldCheck,
  Search,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "mc_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  const headers = token ? { "X-Admin-Password": token } : {};

  const login = async (e) => {
    e.preventDefault();
    if (!pw) return toast.error("Ingresa la contraseña.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      const tk = data.token;
      localStorage.setItem(STORAGE_KEY, tk);
      setToken(tk);
      setPw("");
      toast.success("Sesión iniciada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setContacts([]);
  };

  const fetchContacts = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, { headers });
      setContacts(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada");
        logout();
      } else {
        toast.error("Error al cargar contactos");
      }
    } finally {
      setLoading(false);
    }
  };

  const removeContact = async (id) => {
    if (!confirm("¿Eliminar este lead?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers });
      setContacts((s) => s.filter((c) => c.id !== id));
      toast.success("Lead eliminado");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  useEffect(() => {
    if (token) fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = contacts.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.service?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q)
    );
  });

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-24" data-testid="admin-login-page">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mc-card w-full max-w-md p-10"
        >
          <div className="h-14 w-14 rounded-2xl bg-[color:var(--mc-primary)]/20 border border-[color:var(--mc-cyan)]/30 text-[color:var(--mc-cyan)] flex items-center justify-center mx-auto">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="font-serif-display text-3xl text-white text-center mt-6">
            Panel Admin
          </h1>
          <p className="text-white/65 text-center text-sm mt-2">
            Acceso restringido al equipo de Master Credit.
          </p>

          <label className="overline text-[color:var(--mc-cyan)] mt-8 block">Contraseña</label>
          <div className="relative mt-2">
            <Lock className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mc-input pl-11"
              placeholder="••••••••"
              autoFocus
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full mt-6 justify-center disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {loading ? "Validando..." : "Entrar"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] px-6 lg:px-10 py-16 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="overline text-[color:var(--mc-gold)]">Panel Admin</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white mt-2">
            Leads recibidos
          </h1>
          <p className="text-white/65 mt-3">
            {contacts.length} mensaje{contacts.length === 1 ? "" : "s"} en total
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchContacts}
            className="btn-ghost text-sm"
            disabled={loading}
            data-testid="admin-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <button onClick={logout} className="btn-ghost text-sm" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </div>

      <div className="mt-8 relative">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, email, servicio..."
          className="mc-input pl-11"
          data-testid="admin-search"
        />
      </div>

      <div className="mt-8 grid gap-3" data-testid="admin-contacts-list">
        {filtered.length === 0 && (
          <div className="mc-card p-10 text-center text-white/65">
            {loading ? "Cargando..." : "No hay leads aún."}
          </div>
        )}

        <AnimatePresence>
          {filtered.map((c) => {
            const isOpen = expanded === c.id;
            return (
              <motion.div
                key={c.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="mc-card p-6"
                data-testid={`admin-contact-${c.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-heading text-lg text-white truncate">{c.name}</p>
                      {c.service && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--mc-gold)] bg-[color:var(--mc-gold)]/10 border border-[color:var(--mc-gold)]/30 px-2.5 py-1 rounded-full">
                          {c.service}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm text-white/70 flex-wrap">
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[color:var(--mc-cyan)]">
                        <Mail className="h-3.5 w-3.5" /> {c.email}
                      </a>
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[color:var(--mc-cyan)]">
                          <Phone className="h-3.5 w-3.5" /> {c.phone}
                        </a>
                      )}
                      <span className="text-white/45 text-xs">
                        {new Date(c.created_at).toLocaleString("es-MX")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setExpanded(isOpen ? null : c.id)}
                      className="h-9 w-9 rounded-full border border-white/15 hover:border-[color:var(--mc-cyan)] text-white/70 hover:text-[color:var(--mc-cyan)] flex items-center justify-center transition-colors"
                      aria-label="Expandir"
                      data-testid={`admin-expand-${c.id}`}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <button
                      onClick={() => removeContact(c.id)}
                      className="h-9 w-9 rounded-full border border-white/15 hover:border-red-500/60 text-white/70 hover:text-red-400 flex items-center justify-center transition-colors"
                      aria-label="Eliminar"
                      data-testid={`admin-delete-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="mt-5 pt-5 border-t border-white/10 text-white/85 leading-relaxed whitespace-pre-wrap">
                        {c.message}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
